// Reading the archive back: resolve a chat to its messages by pulling the
// encrypted chunk directory, then the chunks it names.

import * as F from './format';
import { api } from './api';
import type { ParsedMessage } from './parser';

export interface MemberMeta { plainLength: number; frameSize: number; mime: string }

const MIME: Record<number, string> = {
  [F.KIND.IMAGE]: 'image/jpeg', [F.KIND.VIDEO]: 'video/mp4',
  [F.KIND.AUDIO]: 'audio/ogg', [F.KIND.DOCUMENT]: 'application/octet-stream',
};

async function inflate(bytes: Uint8Array): Promise<Uint8Array> {
  const s = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(s).arrayBuffer());
}

/** Fetch and decrypt one whole member through the API. */
async function member(accountKey: Uint8Array, cid: string, plainLength: number, frameSize: number) {
  const cache = new Map<number, Uint8Array>();
  const read = async (offset: number, length: number) => {
    const hit = cache.get(offset);
    if (hit) return hit;
    const got = await api.mediaRange(cid, offset, offset + length - 1);
    cache.set(offset, got);
    return got;
  };
  return F.decryptRange(accountKey, F.fromHex(cid), plainLength, frameSize, read, 0, plainLength - 1);
}

/**
 * The manifest lives with the client. The reference server does not hand back
 * member geometry yet, so the importer stashes it locally; A4 returns it with
 * the chat and this reads it from there instead.
 */
export function rememberManifest(entries: Record<string, MemberMeta>) {
  const all = { ...loadManifest(), ...entries };
  localStorage.setItem('wa.manifest', JSON.stringify(all));
  return all;
}
export function loadManifest(): Record<string, MemberMeta> {
  try { return JSON.parse(localStorage.getItem('wa.manifest') ?? '{}'); } catch { return {}; }
}
export const mimeFor = (kind: number) => MIME[kind] ?? 'application/octet-stream';

/** Geometry from the API, falling back to nothing — the server knows where
 *  every member is, so an archive opens in a browser that never imported it. */
async function geometry(cid: string): Promise<MemberMeta> {
  const cached = loadManifest()[cid];
  if (cached) return cached;
  const loc = await api.mediaLocation(cid);
  return { plainLength: loc.plainLength, frameSize: loc.frameSize, mime: mimeFor(loc.kind) };
}

/**
 * The media type of a stored member, for a browser that did not import it.
 *
 * The transcript's filename is dropped when a chunk is written — the content
 * id replaces it — so the only local record of what a member *is* lives in the
 * manifest the importing browser saved. On any other device that is empty, and
 * every photo rendered as a generic "attachment" link.
 *
 * The server knows, and says so on the endpoint the reader has to call anyway.
 * Answers are folded into the manifest, so each member costs one request once.
 */
export async function mimeForMember(cid: string): Promise<string> {
  const known = loadManifest()[cid];
  if (known) return known.mime;
  const meta = await geometry(cid);
  rememberManifest({ [cid]: meta });
  return meta.mime;
}

/**
 * The last thing said in a chat, for the list.
 *
 * WhatsApp's own list shows a preview, and an archive without one is just a
 * row of titles. The text is encrypted like everything else, so it has to be
 * decrypted here — but only the final chunk, not the conversation: a directory
 * lists its chunks in order, and the tail of the last one is the tail of the
 * chat. One member fetch per chat, once, cached.
 */
const previews = new Map<string, { who?: string; text: string } | null>();

export async function lastMessage(accountKey: Uint8Array, directoryCid: string) {
  if (previews.has(directoryCid)) return previews.get(directoryCid) ?? null;
  try {
    const dirMeta = await geometry(directoryCid);
    const dirBytes = await member(accountKey, directoryCid, dirMeta.plainLength, dirMeta.frameSize);
    const dir = JSON.parse(new TextDecoder().decode(await inflate(dirBytes))) as {
      chunks: Array<{ id: string }>;
    };
    const last = dir.chunks.at(-1);
    if (!last) { previews.set(directoryCid, null); return null; }

    const meta = await geometry(last.id);
    const bytes = await member(accountKey, last.id, meta.plainLength, meta.frameSize);
    const msgs = JSON.parse(new TextDecoder().decode(await inflate(bytes))) as ParsedMessage[];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i]!;
      const text = m.body ?? (m.attach || m.file ? 'Attachment' : m.omitted ? 'Attachment' : '');
      if (text) {
        const out = { who: m.sender, text };
        previews.set(directoryCid, out);
        return out;
      }
    }
    previews.set(directoryCid, null);
    return null;
  } catch {
    previews.set(directoryCid, null);
    return null;
  }
}

/**
 * The tail of a conversation: the newest `chunks` chunks, newest first.
 *
 * The media panel used to call `loadMessages`, which decrypts a conversation
 * whole. Across an archive that is every byte of it, on a phone, to build a
 * grid nobody scrolls to the bottom of. A directory lists its chunks in order,
 * so the recent end of a conversation is the last chunk or two, and asking for
 * the tail costs one fetch per conversation instead of all of them.
 *
 * @param skip how many chunks from the end to leave out, so the caller can ask
 *   for the next page without re-reading what it already has.
 */
export async function tailMessages(
  accountKey: Uint8Array, directoryCid: string, chunks = 1, skip = 0,
): Promise<{ messages: ParsedMessage[]; more: boolean }> {
  const dirMeta = await geometry(directoryCid);
  const dirBytes = await member(accountKey, directoryCid, dirMeta.plainLength, dirMeta.frameSize);
  const dir = JSON.parse(new TextDecoder().decode(await inflate(dirBytes))) as {
    chunks: Array<{ id: string }>;
  };
  const end = dir.chunks.length - skip;
  const start = Math.max(0, end - chunks);
  if (end <= 0) return { messages: [], more: false };

  const learned: Record<string, MemberMeta> = {};
  const parts = await Promise.all(dir.chunks.slice(start, end).map(async (c) => {
    const meta = await geometry(c.id);
    learned[c.id] = meta;
    const bytes = await member(accountKey, c.id, meta.plainLength, meta.frameSize);
    return JSON.parse(new TextDecoder().decode(await inflate(bytes))) as ParsedMessage[];
  }));
  rememberManifest(learned);
  return { messages: parts.flat(), more: start > 0 };
}

/**
 * Everything a conversation refers to: its directory, its chunks, and the
 * media inside them.
 *
 * This is what the server cannot work out for itself — the directory is
 * encrypted, and a content id comes from the bytes, so the same photo in two
 * conversations is one member row. Registering the list is what makes deleting
 * a conversation able to free anything at all.
 */
export async function allMembersOf(
  accountKey: Uint8Array, directoryCid: string,
): Promise<string[]> {
  const meta = await geometry(directoryCid);
  const bytes = await member(accountKey, directoryCid, meta.plainLength, meta.frameSize);
  const dir = JSON.parse(new TextDecoder().decode(await inflate(bytes))) as {
    chunks: Array<{ id: string }>;
  };
  const ids = new Set<string>([directoryCid, ...dir.chunks.map((c) => c.id)]);
  // The media ids live inside the chunks, not the directory, so this is the
  // same decrypt the reading path does — which is why the client registers a
  // chat at the moment it opens it.
  for (const m of await loadMessages(accountKey, directoryCid)) {
    if (m.attach) ids.add(m.attach);
  }
  return [...ids];
}

/**
 * @param onPartial called once with the newest chunk, before the rest arrive.
 *
 * Opening a chat used to wait for every chunk of it. But the bottom of the
 * conversation is what anyone looks at first, and that is one chunk — so the
 * last one is fetched alone, drawn, and the history fills in behind it. A ten
 * thousand message chat stops costing twenty round trips before anything
 * appears and starts costing two.
 */
export async function loadMessages(
  accountKey: Uint8Array, directoryCid: string,
  onPartial?: (messages: ParsedMessage[]) => void,
): Promise<ParsedMessage[]> {
  const dirMeta = await geometry(directoryCid);
  const dirBytes = await member(accountKey, directoryCid, dirMeta.plainLength, dirMeta.frameSize);
  const inflated = await inflate(dirBytes);
  const dir = JSON.parse(new TextDecoder().decode(inflated)) as {
    chunks: Array<{ id: string; from: number; to: number; n: number }>;
  };

  // Chunks were fetched one after another: for each, a round trip for its
  // geometry, another for a signed URL, then the bytes — all serial. On a
  // browser that had not done the import, where nothing is cached locally,
  // opening a long chat meant hundreds of sequential round trips and a wait
  // long enough to look broken.
  //
  // Bounded rather than unbounded: a chat with hundreds of chunks would
  // otherwise open hundreds of connections at once and be slower again, on top
  // of asking the API to sign that many URLs simultaneously.
  const LANES = 6;
  const parts: ParsedMessage[][] = new Array(dir.chunks.length);
  const learned: Record<string, MemberMeta> = {};

  const fetchChunk = async (i: number) => {
    const c = dir.chunks[i]!;
    const meta = await geometry(c.id);
    learned[c.id] = meta;
    const bytes = await member(accountKey, c.id, meta.plainLength, meta.frameSize);
    parts[i] = JSON.parse(new TextDecoder().decode(await inflate(bytes))) as ParsedMessage[];
  };

  // The newest chunk on its own first. Skipped when there is only one, which
  // would otherwise be fetched, drawn, and then drawn again identically.
  const lastIndex = dir.chunks.length - 1;
  if (onPartial && dir.chunks.length > 1) {
    await fetchChunk(lastIndex);
    onPartial(parts[lastIndex]!);
  }

  let next = 0;

  await Promise.all(Array.from({ length: Math.min(LANES, dir.chunks.length) }, async () => {
    for (;;) {
      const i = next++;
      if (i >= dir.chunks.length) return;
      if (parts[i]) continue;   // the newest one, already fetched above
      await fetchChunk(i);
    }
  }));

  rememberManifest(learned);
  // Order matters: the chunks are a conversation, not a set.
  return parts.flat();
}
